import {
  HOME_SET_VIEW_ROLE,
  HOME_SET_WIDGET_PREFERENCES,
  HOME_UPDATE_WIDGET_PREFERENCE,
  HOME_FETCH_POSTS_REQUEST,
  HOME_FETCH_POSTS_SUCCESS,
  HOME_FETCH_POSTS_FAILURE,
  HOME_FETCH_SPACES_REQUEST,
  HOME_FETCH_SPACES_SUCCESS,
  HOME_FETCH_SPACES_FAILURE,
  HOME_UPDATE_POST_COUNTS,
} from '@core/constants/actions';
import { HomeViewRole } from '@app/constants/home';
import {
  HomeSpaceApiItem,
  HomeSpaceEngagementApiItem,
  HomeSpaceProgressApiItem,
  HomeState,
  WidgetPreference,
} from '@core/types/state';

const initialState: HomeState = {
  viewRole: null,
  widgetPreferences: null,
  posts: [],
  postsLoading: false,
  postsHasMore: true,
  postsCursor: null,
  studentPostOnly: false,
  spaces: [],
  spacesProgressById: {},
  spacesEngagementById: {},
  spacesLoading: false,
  spacesHasMore: true,
  spacesCursor: null,
  spacesError: false,
  spacesErrorType: null,
};

type HomeSetViewRoleAction = {
  type: typeof HOME_SET_VIEW_ROLE;
  viewRole: HomeViewRole;
};

type HomeSetWidgetPreferencesAction = {
  type: typeof HOME_SET_WIDGET_PREFERENCES;
  preferences: WidgetPreference[];
};

type HomeUpdateWidgetPreferenceAction = {
  type: typeof HOME_UPDATE_WIDGET_PREFERENCE;
  widgetId: string;
  isHidden: boolean;
};

type HomeFetchPostsRequestAction = {
  type: typeof HOME_FETCH_POSTS_REQUEST;
  reset?: boolean;
  studentPost?: boolean;
};

type HomeFetchPostsSuccessAction = {
  type: typeof HOME_FETCH_POSTS_SUCCESS;
  posts: any[];
  cursor: string | null;
  hasMore: boolean;
  reset?: boolean;
};

type HomeFetchPostsFailureAction = {
  type: typeof HOME_FETCH_POSTS_FAILURE;
};

type HomeFetchSpacesRequestAction = {
  type: typeof HOME_FETCH_SPACES_REQUEST;
};

type HomeFetchSpacesSuccessAction = {
  type: typeof HOME_FETCH_SPACES_SUCCESS;
  spaces: HomeSpaceApiItem[];
  progress: HomeSpaceProgressApiItem[];
  engagement: HomeSpaceEngagementApiItem[];
  cursor: string | null;
  hasMore: boolean;
};

type HomeFetchSpacesFailureAction = {
  type: typeof HOME_FETCH_SPACES_FAILURE;
  errorType?: 'network' | 'api';
};

type HomeUpdatePostCountsAction = {
  type: typeof HOME_UPDATE_POST_COUNTS;
  postId: string;
  commentsCount: number;
  reactionsCount: number;
};

type HomeAction =
  | HomeSetViewRoleAction
  | HomeSetWidgetPreferencesAction
  | HomeUpdateWidgetPreferenceAction
  | HomeFetchPostsRequestAction
  | HomeFetchPostsSuccessAction
  | HomeFetchPostsFailureAction
  | HomeFetchSpacesRequestAction
  | HomeFetchSpacesSuccessAction
  | HomeFetchSpacesFailureAction
  | HomeUpdatePostCountsAction;

const mergeBySpaceId = <T extends { spaceId: string }>(
  current: Record<string, T>,
  items: T[]
) => {
  const next = { ...current };

  items.forEach((item) => {
    next[item.spaceId] = item;
  });

  return next;
};

const home = (
  state: HomeState = initialState,
  action: HomeAction
): HomeState => {
  switch (action.type) {
    case HOME_SET_VIEW_ROLE:
      return {
        ...state,
        viewRole: action.viewRole,
      };
    case HOME_SET_WIDGET_PREFERENCES:
      return {
        ...state,
        widgetPreferences: action.preferences,
      };
    case HOME_UPDATE_WIDGET_PREFERENCE:
      if (!state.widgetPreferences) return state;
      return {
        ...state,
        widgetPreferences: state.widgetPreferences.map((pref) =>
          pref.widgetId === action.widgetId
            ? {
                ...pref,
                isHidden: action.isHidden,
              }
            : pref
        ),
      };
    case HOME_FETCH_POSTS_REQUEST:
      return {
        ...state,
        postsLoading: true,
        posts: action.reset ? [] : state.posts,
        postsCursor: action.reset ? null : state.postsCursor,
        postsHasMore: action.reset ? true : state.postsHasMore,
        studentPostOnly: action.reset
          ? !!action.studentPost
          : state.studentPostOnly,
      };
    case HOME_FETCH_POSTS_SUCCESS:
      return {
        ...state,
        postsLoading: false,
        posts: action.reset ? action.posts : [...state.posts, ...action.posts],
        postsCursor: action.cursor,
        postsHasMore: action.hasMore,
      };
    case HOME_FETCH_POSTS_FAILURE:
      return {
        ...state,
        postsLoading: false,
      };
    case HOME_FETCH_SPACES_REQUEST:
      return {
        ...state,
        spacesLoading: true,
        spacesError: false,
        spacesErrorType: null,
      };
    case HOME_FETCH_SPACES_SUCCESS: {
      const existingIds = new Set(state.spaces.map((space) => space.spaceId));
      const newSpaces = action.spaces.filter(
        (space) => !existingIds.has(space.spaceId)
      );

      return {
        ...state,
        spacesLoading: false,
        spaces: [...state.spaces, ...newSpaces],
        spacesProgressById: mergeBySpaceId(
          state.spacesProgressById,
          action.progress
        ),
        spacesEngagementById: mergeBySpaceId(
          state.spacesEngagementById,
          action.engagement
        ),
        spacesCursor: action.cursor,
        spacesHasMore: action.hasMore,
      };
    }
    case HOME_FETCH_SPACES_FAILURE:
      return {
        ...state,
        spacesLoading: false,
        spacesError: true,
        spacesErrorType: action.errorType ?? 'api',
      };
    case HOME_UPDATE_POST_COUNTS:
      return {
        ...state,
        posts: state.posts.map((post: any) => {
          const postId = post?.objectId || post?.id;
          if (postId === action.postId) {
            return {
              ...post,
              _commentsCount: action.commentsCount,
              _reactionsCount: action.reactionsCount,
            };
          }
          return post;
        }),
      };
    default:
      return state;
  }
};

export default home;
